import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, CheckCircle, AlertCircle, Clock, Loader, Building2 } from 'lucide-react';

import Button from '../../components/ui/Button';
import useImportStatusPolling from '../../hooks/useImportStatusPolling';
import {
  Container,
  StickyHeader,
  Title,
  StepIndicator,
  StepDot,
  StepLine,
  StepLabel,
  Card,
  DropZone,
  DropText,
  FileName,
  ErrorList,
  ErrorItem,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  TagList,
  Tag,
  Footer,
  HistoryTable,
  StatusBadge,
  TargetBanner,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getImportTemplate,
  validateImport,
  executeImport,
  getImportStatus,
  getImportHistory,
} from '../../services/importService';
import { useAuth } from '../../context/AuthContext';

export default function ImportPage() {
  const { t } = useTranslation();
  const { accessibleTenants, activeTenantId, canSwitchTenant } = useAuth();
  const activeTenant = accessibleTenants.find((tn) => tn.id === activeTenantId);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const fileInputRef = useRef(null);
  const { importStatus, statusLost, startPolling, retryPolling, resetPolling } = useImportStatusPolling(getImportStatus);

  // Load import history on mount
  useEffect(() => {
    getImportHistory({ pageNumber: 1, pageSize: 10 })
      .then((res) => setHistory(res.data || []))
      .catch(() => {});
  }, [importStatus?.status]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const res = await getImportTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'flexbee-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Handled by interceptor
    }
  }, []);

  const handleFileSelect = useCallback((selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith('.xlsx')) {
      setFile(selectedFile);
      setValidation(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleValidate = useCallback(async () => {
    if (!file) return;
    setValidating(true);
    try {
      const res = await validateImport(file);
      setValidation(res.data);
      if (res.data.errors?.length === 0) {
        setStep(3);
      }
    } catch {
      // Handled by interceptor
    } finally {
      setValidating(false);
    }
  }, [file]);

  const handleExecute = useCallback(async () => {
    if (!validation?.importId) return;
    setExecuting(true);
    try {
      await executeImport(validation.importId, file?.name || '', validation.totalRows);
      setStep(4);

      startPolling(validation.importId);
    } catch {
      // Handled by interceptor
    } finally {
      setExecuting(false);
    }
  }, [validation, file?.name, startPolling]);

  const handleReset = useCallback(() => {
    setStep(1);
    setFile(null);
    setValidation(null);
    resetPolling();
  }, [resetPolling]);

  const steps = [
    { label: t('import.step1') },
    { label: t('import.step2') },
    { label: t('import.step3') },
    { label: t('import.step4') },
  ];

  // C-2 düzeltmesi: sonuç adımı eskiden ham `err.message` basıyordu (t() YOK) ve
  // uyarıları hatalardan ayırmıyordu — doğrulama adımının deseni burada tekrarlanıyor.
  // Bazı mesajlar "import.error.unexpected: <detay>" gibi son ekli; yalnız ':' öncesi
  // anahtar kısmı çevrilir, kalan ham detay olduğu gibi eklenir.
  const translateImportMessage = useCallback((message) => {
    if (!message) return message;
    const sepIdx = message.indexOf(': ');
    if (sepIdx === -1) return t(message);
    return t(message.slice(0, sepIdx)) + message.slice(sepIdx);
  }, [t]);

  const resultErrors = (importStatus?.errors || []).filter((err) => err.severity !== 'warning');
  const resultWarnings = (importStatus?.errors || []).filter((err) => err.severity === 'warning');

  return (
    <Container>
      <StickyHeader>
        <Title>{t('import.title')}</Title>
        {activeTenant && (
          <TargetBanner>
            <Building2 size={18} />
            <span>
              {t(
                canSwitchTenant ? 'import.targetTenantInfoSwitchable' : 'import.targetTenantInfo',
                { tenant: activeTenant.name }
              )}
            </span>
          </TargetBanner>
        )}
        <StepIndicator>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <StepLine $completed={step > i} />}
              <StepDot $active={step === i + 1} $completed={step > i + 1}>
                {step > i + 1 ? '✓' : i + 1}
              </StepDot>
              <StepLabel $active={step === i + 1}>{s.label}</StepLabel>
            </React.Fragment>
          ))}
        </StepIndicator>
      </StickyHeader>

      {/* Step 1: Download Template */}
      {step === 1 && (
        <Card>
          <SectionTitle>{t('import.downloadTemplate')}</SectionTitle>
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginBottom: 16 }}>
            {t('import.downloadTemplateDesc')}
          </p>
          <Button onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('import.downloadButton')}
          </Button>
          <Footer>
            <div />
            <Button onClick={() => setStep(2)}>{t('import.next')}</Button>
          </Footer>
        </Card>
      )}

      {/* Step 2: Upload & Validate */}
      {step === 2 && (
        <Card>
          <SectionTitle>{t('import.uploadFile')}</SectionTitle>

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload size={32} color="#667085" />
            <DropText>
              {file ? (
                <FileName>{file.name}</FileName>
              ) : (
                t('import.dropzone')
              )}
            </DropText>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
          </DropZone>

          {validation && (
            <>
              <SummaryGrid style={{ marginTop: 20 }}>
                <SummaryCard $color="#F0F9FF">
                  <SummaryValue>{validation.totalRows}</SummaryValue>
                  <SummaryLabel>{t('import.totalRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#F0FDF4">
                  <SummaryValue>{validation.newRows}</SummaryValue>
                  <SummaryLabel>{t('import.newRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FFFBEB">
                  <SummaryValue>{validation.updateRows}</SummaryValue>
                  <SummaryLabel>{t('import.updateRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FEF2F2">
                  <SummaryValue>{validation.errors?.length || 0}</SummaryValue>
                  <SummaryLabel>{t('import.errorCount')}</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              {validation.newTitles?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <SectionTitle>{t('import.newTitlesWillCreate')}</SectionTitle>
                  <TagList>
                    {validation.newTitles.map((title) => (
                      <Tag key={title}>{title}</Tag>
                    ))}
                  </TagList>
                </div>
              )}

              {validation.newDepartments?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <SectionTitle>{t('import.newDepartmentsWillCreate')}</SectionTitle>
                  <TagList>
                    {validation.newDepartments.map((dept) => (
                      <Tag key={dept}>{dept}</Tag>
                    ))}
                  </TagList>
                </div>
              )}

              {validation.newLocations?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <SectionTitle>{t('import.newLocationsWillCreate')}</SectionTitle>
                  <TagList>
                    {validation.newLocations.map((loc) => (
                      <Tag key={loc}>{loc}</Tag>
                    ))}
                  </TagList>
                </div>
              )}

              {validation.errors?.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('import.errors')}</SectionTitle>
                  {validation.errors.map((err, i) => (
                    <ErrorItem key={i} $severity="error">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('import.row')} {err.row}: {err.column} — {t(err.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}

              {validation.warnings?.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('import.warnings')}</SectionTitle>
                  {validation.warnings.map((warn, i) => (
                    <ErrorItem key={i} $severity="warning">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('import.row')} {warn.row}: {warn.column} — {t(warn.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}
            </>
          )}

          <Footer>
            <Button variant="ghost" onClick={() => setStep(1)}>{t('import.back')}</Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={!file || validating}
              >
                {validating ? <Loader size={16} className="spin" /> : null}
                {t('import.validate')}
              </Button>
              {validation && validation.errors?.length === 0 && (
                <Button onClick={() => setStep(3)}>
                  {t('import.next')}
                </Button>
              )}
            </div>
          </Footer>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && validation && (
        <Card>
          <SectionTitle>{t('import.confirmTitle')}</SectionTitle>
          <SummaryGrid>
            <SummaryCard $color="#F0FDF4">
              <SummaryValue>{validation.newRows}</SummaryValue>
              <SummaryLabel>{t('import.willInsert')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#FFFBEB">
              <SummaryValue>{validation.updateRows}</SummaryValue>
              <SummaryLabel>{t('import.willUpdate')}</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          {validation.newTitles?.length > 0 && (
            <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085', marginBottom: 8 }}>
              {t('import.newTitlesNote', { count: validation.newTitles.length })}
            </p>
          )}
          {validation.newDepartments?.length > 0 && (
            <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085', marginBottom: 8 }}>
              {t('import.newDepartmentsNote', { count: validation.newDepartments.length })}
            </p>
          )}

          <Footer>
            <Button variant="ghost" onClick={() => setStep(2)}>{t('import.back')}</Button>
            <Button onClick={handleExecute} disabled={executing}>
              {executing ? <Loader size={16} /> : <CheckCircle size={16} style={{ marginRight: 8 }} />}
              {t('import.startImport')}
            </Button>
          </Footer>
        </Card>
      )}

      {/* Step 4: Progress & Result */}
      {step === 4 && (
        <Card>
          {!importStatus || importStatus.status === 'queued' || importStatus.status === 'processing' ? (
            statusLost ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <AlertCircle size={40} color="#D97706" />
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
                  {t('import.statusUnavailable')}
                </p>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085' }}>
                  {t('import.statusUnavailableDesc')}
                </p>
                <Button variant="secondary" onClick={retryPolling}>
                  {t('import.statusRetry')}
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Loader size={40} color="#0291EF" className="spin" />
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
                  {t('import.processing')}
                </p>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085' }}>
                  {t('import.processingDesc')}
                </p>
              </div>
            )
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                {importStatus.status === 'completed' ? (
                  <CheckCircle size={48} color="#16a34a" />
                ) : (
                  <AlertCircle size={48} color="#DC2626" />
                )}
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 18, fontWeight: 700, marginTop: 12 }}>
                  {importStatus.status === 'completed' ? t('import.completed') : t('import.failed')}
                </p>
              </div>

              <SummaryGrid>
                <SummaryCard $color="#F0F9FF">
                  <SummaryValue>{importStatus.totalRows}</SummaryValue>
                  <SummaryLabel>{t('import.totalRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#F0FDF4">
                  <SummaryValue>{importStatus.insertedRows}</SummaryValue>
                  <SummaryLabel>{t('import.inserted')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FFFBEB">
                  <SummaryValue>{importStatus.updatedRows}</SummaryValue>
                  <SummaryLabel>{t('import.updated')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FEF2F2">
                  <SummaryValue>{importStatus.errorRows}</SummaryValue>
                  <SummaryLabel>{t('import.errorCount')}</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              {resultErrors.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('import.errors')}</SectionTitle>
                  {resultErrors.map((err, i) => (
                    <ErrorItem key={i} $severity="error">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('import.row')} {err.row}: {err.column} — {translateImportMessage(err.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}

              {resultWarnings.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('import.warnings')}</SectionTitle>
                  {resultWarnings.map((warn, i) => (
                    <ErrorItem key={i} $severity="warning">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('import.row')} {warn.row}: {warn.column} — {translateImportMessage(warn.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}

              <Footer>
                <div />
                <Button onClick={handleReset}>{t('import.newImport')}</Button>
              </Footer>
            </>
          )}
        </Card>
      )}

      {/* Import History */}
      {history.length > 0 && (
        <Card style={{ marginTop: 32 }}>
          <SectionTitle>{t('import.history')}</SectionTitle>
          <HistoryTable>
            <thead>
              <tr>
                <th>{t('import.fileName')}</th>
                <th>{t('import.status')}</th>
                <th>{t('import.totalRows')}</th>
                <th>{t('import.inserted')}</th>
                <th>{t('import.updated')}</th>
                <th>{t('import.errorCount')}</th>
                <th>{t('import.date')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td>{item.fileName}</td>
                  <td><StatusBadge $status={item.status}>{t(`import.status_${item.status}`)}</StatusBadge></td>
                  <td>{item.totalRows}</td>
                  <td>{item.insertedRows}</td>
                  <td>{item.updatedRows}</td>
                  <td>{item.errorRows}</td>
                  <td>{new Date(item.createDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        </Card>
      )}
    </Container>
  );
}
